import PropTypes from "prop-types";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import RuvixxDate from "components/RuvixxDate";
import RegistrantLeadStatusCard from "routes/leads/swoogo_registrants/RegistrantLeadStatusCard";
import useCanReadRegistrantLeads from "routes/leads/swoogo_registrants/useCanReadRegistrantLeads";
import {
  DetailGrid,
  DetailRow,
  OverviewSkeleton,
  RecordLink,
} from "routes/swoogo/overviewPrimitives";
import RegistrantSessionsCard from "routes/swoogo/RegistrantSessionsCard";

const RegistrantOverview = ({ registrant, isLoading }) => {
  const canReadLeads = useCanReadRegistrantLeads();

  if (isLoading || !registrant) {
    return <OverviewSkeleton />;
  }

  const customFields = registrant.custom_fields ?? [];

  return (
    <div className="tw:flex tw:w-full tw:max-w-7xl tw:flex-col tw:gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Registration</CardTitle>
        </CardHeader>
        <CardContent>
          <DetailGrid>
            <DetailRow label="Status">
              {registrant.registration_status ? (
                <Badge variant="secondary">
                  {registrant.registration_status}
                </Badge>
              ) : null}
            </DetailRow>
            <DetailRow label="Event">
              {registrant.event_name ? (
                <RecordLink to={`/swoogo/events/${registrant.event_id}`}>
                  {registrant.event_name}
                </RecordLink>
              ) : null}
            </DetailRow>
            <DetailRow label="Registered">
              {registrant.registered_at ? (
                <RuvixxDate date={registrant.registered_at} />
              ) : null}
            </DetailRow>
            <DetailRow label="Checked In">
              {registrant.checked_in_at ? (
                <RuvixxDate date={registrant.checked_in_at} />
              ) : null}
            </DetailRow>
            <DetailRow label="Email">{registrant.email}</DetailRow>
            <DetailRow label="Notes">{registrant.notes || null}</DetailRow>
          </DetailGrid>
        </CardContent>
      </Card>

      {canReadLeads ? (
        <RegistrantLeadStatusCard registrant={registrant} />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Contact</CardTitle>
        </CardHeader>
        <CardContent>
          <DetailGrid>
            <DetailRow label="Linked Contact">
              {registrant.contact_id ? (
                <RecordLink to={`/contacts/${registrant.contact_id}`}>
                  {registrant.contact_name}
                </RecordLink>
              ) : (
                <span className="tw:text-muted-foreground">Not linked</span>
              )}
            </DetailRow>
            <DetailRow label="Link Chosen Manually">
              {registrant.contact_linked_manually ? "Yes" : "No"}
            </DetailRow>
          </DetailGrid>
        </CardContent>
      </Card>

      <RegistrantSessionsCard registrant={registrant} />

      {customFields.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Custom Fields</CardTitle>
          </CardHeader>
          <CardContent>
            <DetailGrid>
              {customFields.map(field => (
                <DetailRow key={field.attribute} label={field.label}>
                  {field.value}
                </DetailRow>
              ))}
            </DetailGrid>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

RegistrantOverview.propTypes = {
  registrant: PropTypes.shape({
    id: PropTypes.number,
    event_id: PropTypes.number,
    registration_status: PropTypes.string,
    event_name: PropTypes.string,
    registered_at: PropTypes.string,
    checked_in_at: PropTypes.string,
    email: PropTypes.string,
    notes: PropTypes.string,
    contact_id: PropTypes.number,
    contact_name: PropTypes.string,
    contact_linked_manually: PropTypes.bool,
    custom_fields: PropTypes.arrayOf(
      PropTypes.shape({
        attribute: PropTypes.string.isRequired,
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
  }),
  isLoading: PropTypes.bool,
};

export default RegistrantOverview;
